import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { EditAccountTablePagination } from "../../../components/AccountDetailPolicy/editAccountTableControls";
import { scorePercent } from "../AuditHub/utils";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_INPUT_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
  searchFieldSx,
} from "../AuditHub/theme";
import { FAILED_COLOR, PASSED_COLOR, Tile } from "./AuditCardParts";
import { accountAvailability } from "./AuditContextBar";
import { scopeLabel } from "./ControlAudit/ApplyToScopesDialog";

const LINK_COLOR = "#1976d2";

const PREVIEW_LIMIT = 3;
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const SORTS = [
  { value: "lowest", label: "Lowest score first" },
  { value: "highest", label: "Highest score first" },
  { value: "name", label: "Account name (A–Z)" },
];

const byName = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base", numeric: true });

// Unscored accounts have nothing to rank, so they always follow the scored ones.
const byScore = (direction) => (a, b) => {
  const aMissing = a.percent == null;
  const bMissing = b.percent == null;
  if (aMissing || bMissing) return Number(aMissing) - Number(bMissing) || byName(a, b);
  return (a.percent - b.percent) * direction || byName(a, b);
};

const COMPARATORS = { lowest: byScore(1), highest: byScore(-1), name: byName };

const closeXSx = {
  p: 0,
  m: 0,
  border: "none",
  background: "transparent",
  color: AH_WHITE,
  fontSize: 34,
  fontWeight: 900,
  lineHeight: 0.85,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  WebkitTextStroke: `0.6px ${AH_WHITE}`,
};

const greyButtonSx = {
  textTransform: "none",
  fontSize: "0.875rem",
  fontWeight: 600,
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  boxShadow: "none",
  color: AH_WHITE,
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  "&:hover": { backgroundColor: "#5a6268", borderColor: "#5a6268", boxShadow: "none" },
};

const linkButtonSx = {
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 700,
  color: LINK_COLOR,
  minWidth: 0,
  px: 0.75,
  py: 0,
  flexShrink: 0,
  "&:hover": { backgroundColor: "#e3f2fd" },
};

const ScoreBar = ({ percent }) => (
  <Box sx={{ height: 6, borderRadius: 5, overflow: "hidden", display: "flex", bgcolor: FAILED_COLOR }}>
    <Box sx={{ width: `${percent}%`, height: "100%", bgcolor: PASSED_COLOR }} />
  </Box>
);

const ScoreRow = ({ entry, isCurrent, compact }) => {
  const label = (
    <Typography
      sx={{
        fontSize: compact ? 12 : 13,
        fontWeight: isCurrent ? 700 : 500,
        color: isCurrent || !compact ? AH_TEXT : AH_MUTED,
        minWidth: 0,
        ...(compact
          ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
          : { overflowWrap: "anywhere", lineHeight: 1.35 }),
      }}
    >
      {entry.label}
      {isCurrent && !compact ? (
        <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 700, color: LINK_COLOR }}>
          Viewing
        </Box>
      ) : null}
    </Typography>
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: compact
          ? "minmax(0, 1.2fr) minmax(60px, 2fr) 52px"
          : { xs: "minmax(0, 1fr) 72px 48px", sm: "minmax(0, 1fr) 180px 52px" },
        alignItems: "center",
        gap: compact ? 1 : 1.5,
        width: "100%",
        textAlign: "left",
      }}
    >
      {compact ? (
        <Tooltip title={entry.label} placement="top" disableInteractive>
          {label}
        </Tooltip>
      ) : (
        label
      )}
      {entry.percent == null ? (
        <Typography sx={{ gridColumn: "2 / span 2", fontSize: 12, color: AH_MUTED }}>{entry.note}</Typography>
      ) : (
        <>
          <ScoreBar percent={entry.percent} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: AH_TEXT, textAlign: "right" }}>
            {Math.round(entry.percent)}%
          </Typography>
        </>
      )}
    </Box>
  );
};

const AccountScoresDialog = ({ open, onClose, entries, compliance, accountNumber, onSelect }) => {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("lowest");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSort("lowest");
      setPage(0);
    }
  }, [open]);

  const sorted = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = needle ? entries.filter((entry) => entry.search.includes(needle)) : entries.slice();
    return list.sort(COMPARATORS[sort]);
  }, [entries, query, sort]);

  const scoredCount = useMemo(() => entries.filter((entry) => entry.percent != null).length, [entries]);
  const lastPage = Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);
  const pageRows = sorted.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="audit-account-scores-title"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "none",
          border: `1px solid ${AH_INPUT_BORDER}`,
          height: { sm: "min(680px, calc(100% - 64px))" },
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id="audit-account-scores-title"
            sx={{ fontWeight: 700, fontSize: { xs: "1.125rem", sm: "1.25rem" }, color: AH_WHITE, letterSpacing: "-0.02em", lineHeight: 1.2 }}
          >
            Account Scores
          </Typography>
          <Typography sx={{ mt: 0.75, color: "rgba(255,255,255,0.9)", fontSize: "0.875rem", overflowWrap: "anywhere" }}>
            {compliance} · {scoredCount} of {entries.length} accounts scored
          </Typography>
        </Box>
        <Box component="button" type="button" aria-label="Close" onClick={onClose} sx={closeXSx}>
          <span aria-hidden="true">×</span>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          p: { xs: 1.5, sm: 2.5 },
          backgroundColor: AH_TRACK,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <TextField
            size="small"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(0);
            }}
            placeholder="Search by account name or number"
            inputProps={{ "aria-label": "Search accounts" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: AH_MUTED }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...searchFieldSx, flex: "1 1 220px", minWidth: 0 }}
          />
          <TextField
            select
            size="small"
            value={sort}
            onChange={(event) => {
              setSort(event.target.value);
              setPage(0);
            }}
            inputProps={{ "aria-label": "Sort accounts" }}
            sx={{ ...searchFieldSx, flex: { xs: "1 1 100%", sm: "0 0 200px" } }}
          >
            {SORTS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: "0.875rem" }}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: { xs: 240, sm: 0 },
            display: "flex",
            flexDirection: "column",
            backgroundColor: AH_WHITE,
            border: `1px solid ${AH_INPUT_BORDER}`,
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {pageRows.length ? (
              pageRows.map((entry) => {
                const isCurrent = entry.accountNumber === accountNumber;
                const canOpen = (!entry.reason || entry.pendingScan) && !isCurrent;
                return (
                  <Tooltip key={entry.key} title={entry.reason} placement="top" disableInteractive>
                    <Box component="span" sx={{ display: "block" }}>
                      <ButtonBase
                        onClick={() => onSelect(entry.accountNumber)}
                        disabled={!canOpen}
                        sx={{
                          display: "flex",
                          width: "100%",
                          px: 2,
                          py: 1.1,
                          borderBottom: `1px solid ${AH_BORDER}`,
                          backgroundColor: isCurrent ? "#eff6ff" : "transparent",
                          "&:hover": { backgroundColor: "#f8fafc" },
                          "&.Mui-focusVisible": { backgroundColor: "#e3f2fd" },
                          "&.Mui-disabled": { opacity: isCurrent ? 1 : 0.7 },
                        }}
                      >
                        <ScoreRow entry={entry} isCurrent={isCurrent} />
                      </ButtonBase>
                    </Box>
                  </Tooltip>
                );
              })
            ) : (
              <Typography sx={{ p: 3, textAlign: "center", fontSize: "0.875rem", color: AH_MUTED, overflowWrap: "anywhere" }}>
                No accounts match “{query.trim()}”.
              </Typography>
            )}
          </Box>
          {sorted.length > ROWS_PER_PAGE_OPTIONS[0] ? (
            <EditAccountTablePagination
              page={currentPage}
              rowsPerPage={rowsPerPage}
              total={sorted.length}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onPageChange={setPage}
              onRowsPerPageChange={(value) => {
                setRowsPerPage(value);
                setPage(0);
              }}
            />
          ) : null}
        </Box>
      </Box>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${AH_INPUT_BORDER}`, bgcolor: AH_WHITE }}>
        <Button type="button" variant="contained" disableElevation onClick={onClose} sx={greyButtonSx}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Per-account audit scores for one framework. The card shows every account up to PREVIEW_LIMIT; beyond that
 * it shows the lowest-scoring ones and the full list opens in a searchable, paginated dialog.
 */
const AuditAccountScores = ({ accounts, compliance, accountNumber, onAccountChange, sx }) => {
  const [open, setOpen] = useState(false);

  const entries = useMemo(
    () =>
      (Array.isArray(accounts) ? accounts : []).map((account) => {
        const { scope, reason, pendingScan } = accountAvailability(account, compliance);
        return {
          key: account.auditAccountId || account.accountNumber,
          accountNumber: account.accountNumber,
          label: scopeLabel(account),
          search: `${account.accountName || ""} ${account.accountNumber || ""}`.toLowerCase(),
          percent: scope?.hasScanResult ? scorePercent(scope.auditScore) : null,
          note: scope ? "Pending automated Scan completion" : "Framework not assessed",
          reason,
          pendingScan,
        };
      }),
    [accounts, compliance]
  );

  const overflow = entries.length > PREVIEW_LIMIT;
  const preview = useMemo(
    () => (overflow ? entries.slice().sort(COMPARATORS.lowest).slice(0, PREVIEW_LIMIT) : entries),
    [entries, overflow]
  );

  const select = (nextAccountNumber) => {
    setOpen(false);
    if (onAccountChange) onAccountChange(nextAccountNumber);
  };

  return (
    <Tile
      label={overflow ? `Account Scores · Lowest ${PREVIEW_LIMIT} of ${entries.length}` : "Account Scores"}
      action={
        overflow ? (
          <Button size="small" onClick={() => setOpen(true)} sx={linkButtonSx}>
            View all
          </Button>
        ) : null
      }
      sx={sx}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {preview.map((entry) => (
          <ScoreRow key={entry.key} entry={entry} isCurrent={entry.accountNumber === accountNumber} compact />
        ))}
      </Box>
      {overflow ? (
        <AccountScoresDialog
          open={open}
          onClose={() => setOpen(false)}
          entries={entries}
          compliance={compliance}
          accountNumber={accountNumber}
          onSelect={select}
        />
      ) : null}
    </Tile>
  );
};

export default AuditAccountScores;
